"use strict";
/**
 * `Request` module: defines the `appy`'s base types and functions.
 *
 * **Note:**: this module has been designed for internal use; please prefer the re-exported version of `appy/index`.
 *
 * @since 4.0.0
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API|Fetch Api}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch|Global fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch|Using fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Request|Request}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Response|Response}
 * @see {@link https://gcanti.github.io/fp-ts/ReaderTaskEither.html|ReaderTaskEither}
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeReqInput = exports.toResponseError = exports.toRequestError = exports.request = exports.requestAs = void 0;
var E = __importStar(require("fp-ts/Either"));
/**
 * Return a `Req` which will be executed using `fetch()` under the hood.
 *
 * The `data` in the returned `Resp` object is of the type specified in the `type` parameter which is one of [supported `Request` methods](https://developer.mozilla.org/en-US/docs/Web/API/Response#instance_methods).
 *
 * Example:
 * ```ts
 * import {requestAs} from '@contactlab/appy';
 * import {match} from 'fp-ts/Either';
 *
 * // Default method is GET like original `fetch()`
 * const users = requestAs('json')('https://reqres.in/api/users');
 *
 * users().then(
 *   match(
 *     err => console.error(err),
 *     data => console.log(data)
 *   )
 * );
 * ```
 *
 * @category creators
 * @since 5.1.0
 */
var requestAs = function (type) {
    return function (input) {
        return function () {
            var reqInput = (0, exports.normalizeReqInput)(input);
            return fetch.apply(void 0, reqInput).then(function (response) { return __awaiter(void 0, void 0, void 0, function () {
                var data, result;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (!response.ok) {
                                return [2 /*return*/, E.left((0, exports.toResponseError)(new Error("Request responded with status code ".concat(response.status)), response, reqInput))];
                            }
                            return [4 /*yield*/, response[type]()];
                        case 1:
                            data = (_a.sent());
                            result = { response: response, data: data, input: reqInput };
                            return [2 /*return*/, E.right(result)];
                    }
                });
            }); })
                .catch(function (e) { return E.left((0, exports.toRequestError)(E.toError(e), reqInput)); });
        };
    };
};
exports.requestAs = requestAs;
/**
 * Makes a request using `fetch()` under the hood.
 *
 * The `data` in the returned `Resp` object is a `string` because the response's body can **always** be converted to text without error (via [`text()`](https://developer.mozilla.org/en-US/docs/Web/API/Body/text) method).
 *
 * Example:
 * ```ts
 * import {request} from '@contactlab/appy';
 * import {match} from 'fp-ts/Either';
 *
 * // Default method is GET like original `fetch()`
 * const users = request('https://reqres.in/api/users');
 *
 * users().then(
 *   match(
 *     err => console.error(err),
 *     data => console.log(data)
 *   )
 * );
 * ```
 *
 * @category creators
 * @since 4.0.0
 */
exports.request = (0, exports.requestAs)('text');
/**
 * Creates a `RequestError` object.
 *
 * @category Error
 * @since 4.0.0
 */
var toRequestError = function (error, input) { return ({ type: 'RequestError', error: error, input: input }); };
exports.toRequestError = toRequestError;
/**
 * Creates a `ResponseError` object.
 *
 * @category Error
 * @since 4.0.0
 */
var toResponseError = function (error, response, input) { return ({ type: 'ResponseError', response: response, error: error, input: input }); };
exports.toResponseError = toResponseError;
/**
 * Normalizes the input of a `Req` to a `RequestInfoInit` tuple even when only a single `RequestInfo` is provided.
 *
 * @category Request
 * @since 4.0.0
 */
var normalizeReqInput = function (input) {
    return Array.isArray(input) ? input : [input, {}];
};
exports.normalizeReqInput = normalizeReqInput;
//# sourceMappingURL=request.js.map